import { memo } from 'react';
import DocumentTitle from 'react-document-title';

import { Box } from '@hivebrite/design-system/Box';
import { Container } from '@hivebrite/design-system/Container';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';

import SuccessMessageJoinPublicTopic from './TopicActions/SuccessMessageJoinPublicTopic';
import TopicEmptyCard from './TopicEmptyCard';
import TopicsCategories from './TopicsCategories';
import TopicsCategory from './TopicsCategory';
import TopicsFilteredTopics from './TopicsFilteredTopics';
import TopicsMyTopics from './TopicsMyTopics';
import TopicsOtherTopics from './TopicsOtherTopics';
import TopicsSuggestedTopics from './TopicsSuggestedTopics';
import TopicsSuperCategories from './TopicsSuperCategories';
import TopicsUserAuthorization from './TopicsUserAuthorization';
import { GRID } from '../constants/displayAs';
import {
  CATEGORY,
  CATEGORIES,
  FILTERED,
  MY_TOPICS,
  SUGGESTED_TOPICS,
  SUPER_CATEGORY,
} from '../constants/types';
import { useTopicsIndexContext } from '../contexts/TopicsIndexContext';
import useTopicsLists from '../hooks/useTopicsLists';
import useTopicsMenuName from '../hooks/useTopicsMenuName';
import isAuthorizedUser from '../utils/isAuthorizedUser';

const TopicsList = ({
  areFetchedAllCategories,
  canShowEmptyCard,
  currentView,
  isEnabledToPublishSuperCategories,
  rowsToDisplay,
  selectedCategory,
  selectedSuperCategory,
  selectedSuperCategoryId,
}) => {
  const { currentNetwork, currentUser } = useDefaultAppContexts();

  return (
    <>
      {currentView === CATEGORY && (
        <>
          <DocumentTitle
            title={generatePageTitle({
              networkName: getPageTitleFromNetwork(currentNetwork),
              title: selectedCategory?.name,
            })}
          />
          {/* Key force-update TopicCategory when selected category changes */}
          <TopicsCategory
            category={selectedCategory}
            displayAs={GRID}
            key={selectedCategory.id}
          />
        </>
      )}
      {currentView === MY_TOPICS && <TopicsMyTopics displayAs={GRID} />}
      {currentView === SUGGESTED_TOPICS && (
        <TopicsSuggestedTopics displayAs={GRID} />
      )}
      {currentView === FILTERED && <TopicsFilteredTopics />}
      {currentView === CATEGORIES && (
        <>
          {currentUser && <TopicsMyTopics />}
          {currentUser && <TopicsSuggestedTopics />}
          {!isEnabledToPublishSuperCategories && (
            <>
              <TopicsCategories />
              {areFetchedAllCategories && (
                <TopicsOtherTopics rowsToDisplay={rowsToDisplay} />
              )}
            </>
          )}
          {canShowEmptyCard && <TopicEmptyCard />}
        </>
      )}
      {currentView === SUPER_CATEGORY && isEnabledToPublishSuperCategories && (
        <>
          <DocumentTitle
            title={generatePageTitle({
              networkName: getPageTitleFromNetwork(currentNetwork),
              title: selectedSuperCategory?.name,
            })}
          />
          <TopicsCategories key={selectedSuperCategoryId} />
          {selectedSuperCategory?.isDefault && areFetchedAllCategories && (
            <TopicsOtherTopics rowsToDisplay={rowsToDisplay} />
          )}
        </>
      )}
    </>
  );
};

// using this memo helps going down to only 2 render pass (the 2nd one is because rowsToDisplay changes)
const MemoizedTopicsList = memo(
  TopicsList,
  (prev, next) =>
    prev.areFetchedAllCategories === next.areFetchedAllCategories &&
    prev.canShowEmptyCard === next.canShowEmptyCard &&
    prev.currentView === next.currentView &&
    prev.isEnabledToPublishSuperCategories ===
      next.isEnabledToPublishSuperCategories &&
    prev.rowsToDisplay === next.rowsToDisplay &&
    prev.selectedCategory === next.selectedCategory &&
    prev.selectedSuperCategory === next.selectedSuperCategory &&
    prev.selectedSuperCategoryId === next.selectedSuperCategoryId,
);

const TopicsLists = () => {
  const {
    areFetchedAllCategories,
    currentView,
    displaySuccessMessageJoinPublicTopic,
    isEnabledToPublishSuperCategories,
    selectedCategory,
    selectedSuperCategory,
    selectedSuperCategoryId,
    setDisplaySuccessMessageJoinPublicTopic,
    totalAllCategories,
    userAuthorizationInfo,
  } = useTopicsIndexContext();
  const { dataTotalTopics } = useTopicsLists();
  const { totalTopics, isFetchingTopics } = dataTotalTopics;

  const MAX_CATEGORIES_TO_DISPLAY = 6;

  const rowsToDisplay =
    totalAllCategories < MAX_CATEGORIES_TO_DISPLAY
      ? MAX_CATEGORIES_TO_DISPLAY - totalAllCategories
      : 1;

  const canShowEmptyCard =
    totalTopics === undefined && isFetchingTopics === undefined
      ? false
      : totalTopics === 0 && !isFetchingTopics;

  const menuName = useTopicsMenuName();

  return isAuthorizedUser(userAuthorizationInfo.isAuthorized) ? (
    <>
      <ScreenReadersContent as="h1">{menuName}</ScreenReadersContent>
      <Container overflow="hidden" height="auto">
        <Box
          data-testid="topics-lists"
          display="block"
          width="100%"
          height="fit-content"
          mt={9}
        >
          <MemoizedTopicsList
            areFetchedAllCategories={areFetchedAllCategories}
            canShowEmptyCard={canShowEmptyCard}
            currentView={currentView}
            isEnabledToPublishSuperCategories={
              isEnabledToPublishSuperCategories
            }
            rowsToDisplay={rowsToDisplay}
            selectedCategory={selectedCategory}
            selectedSuperCategory={selectedSuperCategory}
            selectedSuperCategoryId={selectedSuperCategoryId}
          />
          <SuccessMessageJoinPublicTopic
            displaySuccessMessage={displaySuccessMessageJoinPublicTopic}
            setDisplaySuccessMessage={() =>
              setDisplaySuccessMessageJoinPublicTopic({})
            }
          />
        </Box>
      </Container>
      {currentView === CATEGORIES && isEnabledToPublishSuperCategories && (
        <TopicsSuperCategories />
      )}
    </>
  ) : (
    <Container overflow="hidden" height="auto">
      <Box display="block" width="100%" height="fit-content" mt={9}>
        <TopicsUserAuthorization />
      </Box>
    </Container>
  );
};

export default TopicsLists;
